<template>
	<div
		:class="[
			'date-picker',
			...this.variantClassList,
			disabled && 'is-disabled',
			hasError && 'has-error'
		]"
	>
		<Label
			class="date-picker__label"
			:labelText="labelText"
			v-if="labelText"
		>
			<template v-slot:leading>
				<slot name="beforeLabel"> </slot>
			</template>

			<template v-slot:trailing>
				<slot name="afterLabel"> </slot>
			</template>
		</Label>
		<div class="date-picker__input">
			<Input
				:id="uid"
				v-model="displayValue"
				:placeholder="placeholderText"
				:hasClearFeature="false"
				type="date"
				:restrictAlphabet="true"
				@change="onInputTextChanged"
				@blur="blurSearchText"
				@searchTextClicked="setExpanded(true)"
				@buttonIconClicked="setExpanded(false)"
				ref="triggerBtn"
				:ariaLabel="ariaLabel"
				:style="datePickerStyle"
				data-testid="date-picker-input"
				autocomplete="off"
			/>
			<div class="date-picker__menu" v-show="expanded" ref="menu">
				<div class="date-picker__calendar-mode" v-show="calendarMode">
					<DatePickerCalendar
						v-for="monthDate in displayMonths"
						:key="monthDate.toDateString()"
						:monthDate="monthDate"
						:value="currentValue"
						:selectionType="datePickerType"
						@todayClicked="onClickToday"
						@clickPreviousMonth="onClickPreviousMonth"
						@clickNextMonth="onClickNextMonth"
						@dayClicked="onDayClicked"
						@monthLabelClick="toggleMode"
						:showPreviousMonthButton="
							getMonthNumber(monthDate) ===
								currentLowCalendarMonthNumber
						"
						:showNextMonthButton="
							getMonthNumber(monthDate) ===
								currentHighCalendarMonthNumber
						"
					/>
					<div class="date-picker__calendar-footer">
						<div class="date-picker__today-btn">
							<ButtomSmall
								variant="text"
								labelText="Today"
								@click="onClickToday"
								iconClassName="icon-schedule"
							/>
						</div>
						<ButtonSet>
							<template #default>
								<ButtonClassic
									variant="primary"
									labelText="Done"
									@click="setExpanded(false)"
									data-testid="done-btn"
								/>
								<ButtonClassic
									variant="text"
									labelText="Clear"
									@click="onClickClear"
									data-testid="clear-btn"
								/>
							</template>
						</ButtonSet>
					</div>
				</div>
				<div
					class="date-picker__months-years-mode"
					v-show="monthYearMode"
				>
					<DatePickerMonths v-model="tempSelectedMonth" />

					<span class="date-picker__vertical-divider">
						<Divider variant="vertical" />
					</span>

					<DatePickerYears v-model="tempSelectedYear" />

					<span class="date-picker__divider">
						<Divider />
					</span>
					<!-- TODO: The following CSS for the "Back to Calendar" and "Done" buttons needs to be cleaned up -->
					<div style="grid-column:1/6;">
						<div
							class="date-picker__back-to-calendar-btn"
							style="display:inline-grid;"
						>
							<ButtonClassic
								:iconLeading="true"
								iconLeadingClassName="icon-arrow_left"
								variant="text"
								labelText="Back to Calendar"
								@click="onBackToCalendarClicked"
							/>
						</div>
						<div style="display:inline-grid; float:right;">
							<ButtonClassic
								variant="text"
								labelText="Done"
								@click="onMonthsYearsDoneClicked"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
		<PopperPosition
			trackedRefName="triggerBtn"
			popoverRefName="menu"
			placement="bottom-start"
			:popperVisible="expanded"
			strategy="absolute"
		/>
		<HelpFeedback
			class="date-picker__help-feedback"
			:helpText="helpText"
			v-if="helpText"
			:style="datePickerStyle"
		/>
		<ErrorFeedback
			class="date-picker__error-feedback"
			:errorMessages="errorMessages"
			v-if="errorMessages.length > 0"
		/>
	</div>
</template>

<script>
import DatePickerCalendar from './date-picker-calendar.vue';
import DatePickerMonths from './date-picker-months.vue';
import DatePickerYears from './date-picker-years.vue';
import { DATE_RANGE, SINGLE_DATE } from './date-picker-types';
import {
	addMonths,
	compareAsc,
	differenceInCalendarMonths,
	format,
	getMonth,
	getYear,
	isValid
} from 'date-fns';
import Vue from 'vue';
import ButtonClassic from '@/components/forms/button/button-classic.vue';
import ButtomSmall from '@/components/forms/button/button-small.vue';
import ButtonSet from '@/components/forms/button/button-set.vue';
import Input from '@/components/forms/input/input.vue';
import Label from '@/components/forms/shared-stateless-elements/label.vue';
import ErrorFeedback from '@/components/forms/shared-stateless-elements/error-feedback.vue';
import HelpFeedback from '@/components/forms/shared-stateless-elements/help-feedback.vue';
import Divider from '@/components/visual/divider/divider.vue';
import baseComponentMixin from '@/mixins/base-component-mixin';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import variantClasses from './variant-classes/date-picker-variant-classes';
import componentDocumentClickedMixin from '@/mixins/component-document-clicked-mixin';
import PopperPosition from '@/components/visual/popper-position/popper-position.vue';
import formComponentMixin from '@/mixins/form-component-mixin';

export default {
	name: 'DatePicker',
	mixins: [
		baseComponentMixin,
		componentDocumentClickedMixin,
		componentVariantMixin,
		formComponentMixin
	],
	components: {
		DatePickerCalendar,
		Input,
		Label,
		ErrorFeedback,
		HelpFeedback,
		ButtonClassic,
		DatePickerMonths,
		DatePickerYears,
		ButtomSmall,
		ButtonSet,
		Divider,
		PopperPosition
	},
	data() {
		return {
			currentLowCalendarMonth: undefined,
			currentHighCalendarMonth: undefined,
			currentLowCalendarMonthNumber: undefined,
			currentHighCalendarMonthNumber: undefined,
			currentValue: this.value,
			singlePlaceHolderDateFormat: 'MM/DD/YY',
			dateFormat: 'MM/dd/yy',
			displayValue: undefined,
			calendarMode: true,
			monthYearMode: false,
			selectedMonth: undefined,
			tempSelectedMonth: undefined,
			selectedYear: undefined,
			tempSelectedYear: undefined,
			expanded: false,
			dateFormatError: ''
		};
	},
	props: {
		/**
		 * dateRange or singleDate
		 */
		datePickerType: {
			type: String,
			default: DATE_RANGE
		},
		/**
		 * Single Date object or range object in format:
		 * {
		 * 	startDate: Date,
		 * 	endDate: Date
		 * }
		 */
		value: {
			type: [Object, Date]
		},
		labelText: {
			type: String,
			default: null
		},
		displayNumberMonths: {
			type: Number,
			default: 2
		},
		startDate: {
			type: Date,
			default: () => new Date()
		},
		helpText: {
			type: String,
			default: null
		},
		ariaLabel: {
			type: String,
			default: ''
		},
		placeholder: {
			type: String,
			default: undefined
		},
		width: {
			type: [String, Number],
			default: null
		}
	},
	created() {
		this.variantClasses = variantClasses;
		this.setMonths(this.startDate);
		this.documentClickedHandler = this.onDocumentClicked;
	},
	computed: {
		placeHolderDateFormat() {
			let result;

			if (this.datePickerType === SINGLE_DATE) {
				result = this.singlePlaceHolderDateFormat;
			} else {
				result = `${this.singlePlaceHolderDateFormat} - ${this.singlePlaceHolderDateFormat}`;
			}

			return result;
		},
		placeholderText() {
			return this.placeholder || this.placeHolderDateFormat;
		},
		displayMonths() {
			let currentDate = this.currentLowCalendarMonth;
			const months = [currentDate];

			//start at index 1, because we already added the first month
			for (let index = 1; index < this.displayNumberMonths; index++) {
				const nextDate = addMonths(currentDate, 1);

				months.push(nextDate);
				currentDate = nextDate;
			}

			return months;
		},
		datePickerStyle() {
			if (typeof this.width === 'number') {
				return {
					width: `${this.width}px`
				};
			} else {
				return {
					width: this.width
				};
			}
		}
	},
	watch: {
		value: {
			immediate: true,
			handler(newValue) {
				if (this.datePickerType === DATE_RANGE && !newValue) {
					this.currentValue = {
						startDate: null,
						endDate: null
					};
				} else {
					this.currentValue = newValue;
					this.setDisplayValue();
				}
			}
		},
		errorMessages: {
			immediate: true,
			handler(newValue) {
				if (newValue.length > 0) {
					this.$emit('invalidDate', this.labelText, true);
				} else {
					this.$emit('invalidDate', this.labelText, false);
				}
			}
		}
	},
	methods: {
		emitChange() {
			this.$emit('change', this.currentValue);
			this.$emit('input', this.currentValue);
		},
		setExpanded(preventClose) {
			if (preventClose && this.expanded) {
				return;
			} else if (!this.expanded && this.monthYearMode) {
				this.onBackToCalendarClicked();
			} else if (
				this.expanded &&
				this.calendarMode &&
				!this.displayValue
			) {
				this.setMonths(new Date());
			}

			this.expanded = !this.expanded;
			if (!this.expanded) {
				this.$emit('closed');
			}
		},
		setDisplayValue() {
			let value;

			if (this.datePickerType === SINGLE_DATE) {
				value = this.formatDate(this.currentValue);
			} else {
				value = this.formatDateRange(this.currentValue);
			}

			this.displayValue = value;
		},
		formatDateRange(dateRangeObj) {
			let formattedDateRange;

			if (
				dateRangeObj &&
				(isValid(dateRangeObj.startDate) ||
					isValid(dateRangeObj.endDate))
			) {
				if (isValid(dateRangeObj.startDate)) {
					formattedDateRange = this.formatDate(
						dateRangeObj.startDate
					);
				} else {
					formattedDateRange = this.singlePlaceHolderDateFormat;
				}

				formattedDateRange += ' - ';

				if (isValid(dateRangeObj.endDate)) {
					formattedDateRange += this.formatDate(dateRangeObj.endDate);
				} else {
					formattedDateRange += this.singlePlaceHolderDateFormat;
				}
			}

			return formattedDateRange;
		},
		formatDate(date) {
			let formattedDate;

			if (date !== undefined && isValid(date)) {
				formattedDate = format(date, this.dateFormat);
			}

			return formattedDate;
		},
		setMonths(lowCalendarMonth) {
			this.currentLowCalendarMonth = lowCalendarMonth;
			this.currentHighCalendarMonth = addMonths(
				this.currentLowCalendarMonth,
				this.displayNumberMonths - 1
			);
			this.currentLowCalendarMonthNumber = getMonth(
				this.currentLowCalendarMonth
			);
			this.currentHighCalendarMonthNumber = getMonth(
				this.currentHighCalendarMonth
			);

			this.tempSelectedMonth = getMonth(lowCalendarMonth);
			this.selectedMonth = this.tempSelectedMonth;

			this.tempSelectedYear = getYear(lowCalendarMonth);
			this.selectedYear = this.tempSelectedYear;
		},
		onClickPreviousMonth() {
			const lowMonth = addMonths(this.currentLowCalendarMonth, -1);
			this.setMonths(lowMonth);
		},
		onClickNextMonth() {
			const lowMonth = addMonths(this.currentLowCalendarMonth, 1);
			this.setMonths(lowMonth);
		},
		getMonthNumber(date) {
			return getMonth(date);
		},
		blurSearchText() {
			this.$emit('blur', this.currentValue);
		},
		onDayClicked(day) {
			if (this.datePickerType === SINGLE_DATE) {
				this.currentValue = day;
			} else {
				if (
					!this.currentValue.startDate ||
					(compareAsc(day, this.currentValue.startDate) === -1 &&
						!this.currentValue.endDate)
				) {
					Vue.set(this.currentValue, 'startDate', day);
				} else if (!this.currentValue.endDate) {
					Vue.set(this.currentValue, 'endDate', day);
				} else {
					Vue.set(this.currentValue, 'startDate', day);
					this.currentValue.endDate = null;
				}
			}

			this.errorMessageArray = [];
			this.setDisplayValue();
			this.emitChange();
		},
		toggleMode() {
			this.calendarMode = !this.calendarMode;
			this.monthYearMode = !this.monthYearMode;
		},
		onClickToday() {
			const today = new Date();
			this.currentValue = today;
			this.setDisplayValue();
			this.setMonths(today);
			this.errorMessageArray = [];
		},
		onClickClear() {
			if (this.datePickerType === SINGLE_DATE) {
				this.currentValue = {};
			} else {
				Vue.set(this.currentValue, 'startDate', null);
				Vue.set(this.currentValue, 'endDate', null);
			}

			this.displayValue = null;
			this.errorMessageArray = [];

			this.emitChange();
		},
		addCorrectFormat(date) {
			const now = new Date();
			const year = now.getFullYear();
			const month = now.getMonth();
			let tempDate = date;
			let tempArray = [];
			let result = '';

			tempDate = tempDate.replaceAll('/', '');

			switch (tempDate.length) {
				case 1:
					tempArray.push(tempDate.slice(0, 1));
					tempArray.push(month);
					tempArray.push(year);
					break;
				case 2:
					tempArray.push(tempDate.slice(0, 2));
					tempArray.push(month);
					tempArray.push(year);
					break;
				case 3:
					tempArray.push(tempDate.slice(0, 2));
					tempArray.push(tempDate.slice(2, 3));
					tempArray.push(year);
					break;
				case 4:
				case 5:
					tempArray.push(tempDate.slice(0, 2));
					tempArray.push(tempDate.slice(2, 4));
					tempArray.push(year);
					break;
				case 6:
					tempArray.push(tempDate.slice(0, 2));
					tempArray.push(tempDate.slice(2, 4));
					tempArray.push(tempDate.slice(-2));
					break;
				case 7:
					tempArray.push(tempDate.slice(0, 2));
					tempArray.push(tempDate.slice(2, 4));
					tempArray.push(year);
					break;
				case 8:
					tempArray.push(tempDate.slice(0, 2));
					tempArray.push(tempDate.slice(2, 4));
					tempArray.push(tempDate.slice(-4));
					break;
				default:
					tempArray.push(tempDate.slice(0, 2));
					tempArray.push(tempDate.slice(2, 4));
					tempArray.push(tempDate.slice(4, 8));
					break;
			}

			result = tempArray.join('/');

			return result;
		},
		onInputTextChanged() {
			if (
				!this.displayValue ||
				!this.displayValue.trim() ||
				this.displayValue.trim() === '-'
			) {
				this.displayValue = null;
				this.dateFormatError = null;
				this.errorMessageArray = [];

				if (this.dataPickerType === SINGLE_DATE) {
					this.currentValue = null;
				} else {
					Vue.set(this.currentValue, 'startDate', null);
					Vue.set(this.currentValue, 'endDate', null);
				}
				this.emitChange();
				return;
			}

			if (this.datePickerType === SINGLE_DATE) {
				this.displayValue = this.addCorrectFormat(this.displayValue);
				let displayTextDate = new Date(this.displayValue);

				if (!isValid(displayTextDate)) {
					this.dateFormatError = 'Invalid Value Entered for Date';
					this.errorMessageArray.push(this.dateFormatError);
				} else {
					this.dateFormatError = null;
					this.errorMessageArray = [];
					this.currentValue = displayTextDate;
					this.setMonths(displayTextDate);
					this.setDisplayValue();
					this.emitChange();
				}
			} else {
				const displayTextDate = new Date(this.displayValue);

				if (isValid(displayTextDate)) {
					this.dateFormatError = null;
					this.errorMessageArray = [];
					Vue.set(this.currentValue, 'startDate', displayTextDate);
					Vue.set(this.currentValue, 'endDate', null);
					this.setMonths(displayTextDate);
					this.setDisplayValue();
					this.emitChange();
					return;
				}

				const displayTextDates = this.displayValue.split('-');
				const firstDisplayTextDate = new Date(displayTextDates[0]);
				const secondDisplayTextDate = new Date(displayTextDates[1]);

				if (displayTextDates.length !== 2) {
					this.dateFormatError =
						'Invalid Value Entered for Date Range';
					this.errorMessageArray.push(this.dateFormatError);
				} else if (
					isValid(firstDisplayTextDate) &&
					displayTextDates[1].trim() === this.dateFormat.toUpperCase()
				) {
					this.dateFormatError = null;
					this.errorMessageArray = [];
					Vue.set(
						this.currentValue,
						'startDate',
						firstDisplayTextDate
					);
					Vue.set(this.currentValue, 'endDate', null);
					this.setMonths(firstDisplayTextDate);
					this.setDisplayValue();
					this.emitChange();
					return;
				} else if (
					!isValid(firstDisplayTextDate) ||
					!isValid(secondDisplayTextDate)
				) {
					this.dateFormatError =
						'Invalid Value Entered for Date Range';
					this.errorMessageArray.push(this.dateFormatError);
				} else {
					this.dateFormatError = null;
					this.errorMessageArray = [];
					Vue.set(
						this.currentValue,
						'startDate',
						firstDisplayTextDate
					);
					Vue.set(
						this.currentValue,
						'endDate',
						secondDisplayTextDate
					);

					if (
						differenceInCalendarMonths(
							secondDisplayTextDate,
							firstDisplayTextDate
						) === 1
					) {
						this.setMonths(firstDisplayTextDate);
					} else {
						this.setMonths(secondDisplayTextDate);
					}

					this.setDisplayValue();
					this.emitChange();
				}
			}
		},
		onDocumentClicked() {
			if (this.expanded) {
				this.expanded = false;

				if (!this.displayValue) {
					this.setMonths(new Date());
				}
			}
		},
		onBackToCalendarClicked() {
			const today = new Date();

			this.tempSelectedMonth = this.selectedMonth ?? getMonth(today);
			this.tempSelectedYear = this.selectedYear ?? getYear(today);

			this.toggleMode();
		},
		onMonthsYearsDoneClicked() {
			const today = new Date();
			const year = this.tempSelectedYear ?? getYear(today);
			const month = this.tempSelectedMonth ?? getMonth(today);

			this.setMonths(new Date(year, month));
			this.toggleMode();
		}
	}
};
</script>

<style lang="scss" scoped>
.date-picker {
	display: inline-flex;
	flex-direction: column;
	font-size: 13px;

	&__label {
		margin-bottom: $gutter-sm;
	}

	&__menu {
		z-index: $z-level1-5;
		padding: $padding-md;
		background: var(--bg-0);
		color: var(--bg-0-fg-4);
		border: $field-border-width $focus-style var(--neutral-2);
		border-radius: $border-radius;
		@include medium-shadow;
	}

	&__months-years-mode {
		display: grid;
		grid-template-columns: 1fr auto 1fr;
	}

	&__input {
		display: inline-block;
	}
	
	&:deep(.input__field-container){
		border-radius: $border-radius 0 0 $border-radius;
	}

	&:deep(.input__field-container):focus-within::after {
		width: calc(100% + 30px);
	}

	&__calendar-mode {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: $gutter-lg $gutter-xl;
		width: max-content;
	}

	&__divider {
		grid-column: 1/4;
		display: flex;
		padding: $padding-md 0;
	}

	&__vertical-divider {
		padding: 0 $padding-md;
	}

	&__calendar-footer {
		grid-column: 1/3;
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-top: $gutter-lg;
	}

	&.has-error {
		.date-picker__input
			> :first-child:deep(.input__field-container)::after {
			content: '';
			border-color: var(--ui-negative);
		}
	}

	&:deep(.btn-icon) {
		&:hover {
			background-color: var(--bg-brand-2-tint);
			color: var(--bg-brand-2-tint-fg-2);

			.icon:after {
				border: none;
			}
		}
		&:focus,
		&:active {
			outline: $focus-width $focus-style var(--brand-2);
			outline-offset: $focus-offset;

			.icon:after {
				border: none;
			}
		}
	}
}
@include left-label('date-picker');
</style>
